import { useCallback, useEffect, useRef, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { useConfirm } from "@/context/ConfirmContext";

type RecordType = "COMPLAINT" | "SAMPLE";

interface Attachment {
  id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  caption: string | null;
  is_image: boolean;
  created_on: string;
}

const MAX_BYTES = 10 * 1024 * 1024;
const fileUrl = (id: string) => `/api/Attachment/File/${id}`;

/** Photos and PDFs on a complaint or sample: buyer's defect photos, lab reports, shade pictures */
export function RecordPhotosButton({ type, recordId, title, compact, count = 0, onChanged }: { type: RecordType; recordId: string; title: string; compact?: boolean; count?: number; onChanged?: () => void }) {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const { confirm } = useConfirm();
  const perm = type.toLowerCase();
  const canAdd = hasPermission(`${perm}.create`);
  const canDelete = hasPermission(`${perm}.update`);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const input = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(unwrapArray<Attachment>(await api.get(`/api/Attachment/List/${type}/${recordId}`)));
    } catch (err: any) {
      error("Could not load photos", err.message || "Please try again");
    } finally {
      setLoading(false);
    }
  }, [type, recordId, error]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    let done = 0;
    try {
      for (const f of Array.from(files)) {
        if (f.size > MAX_BYTES) {
          error("File too large", `${f.name} is over 10 MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        if (caption.trim()) fd.append("caption", caption.trim());
        await api.upload(`/api/Attachment/Upload/${type}/${recordId}`, fd);
        done += 1;
      }
      if (done) success(done === 1 ? "Photo added" : `${done} files added`, "");
      setCaption("");
      await load();
    } catch (err: any) {
      error("Could not upload", err.message || "Please try again");
      await load();
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async (a: Attachment) => {
    if (!(await confirm({ title: `Remove ${a.file_name}?`, confirmLabel: "Remove", destructive: true }))) return;
    try {
      await api.delete(`/api/Attachment/Delete/${a.id}`);
      setItems((x) => x.filter((i) => i.id !== a.id));
    } catch (err: any) {
      error("Could not remove", err.message || "Please try again");
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" className={`${compact ? "h-7 text-[11px] px-2" : "h-8 text-xs"} gap-1`} onClick={() => setOpen(true)} title="Photos and files">
        <Camera className="size-3.5" /> Photos{count > 0 ? ` (${count})` : ""}
      </Button>
      <Dialog open={open} onOpenChange={(o) => {
        if (busy) return;
        setOpen(o);
        if (!o) onChanged?.();
      }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Photos and files</DialogTitle>
            <DialogDescription>{title}</DialogDescription>
          </DialogHeader>

          {canAdd && (
            <div className="flex flex-wrap items-center gap-2">
              <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional), e.g. shade bar on roll 3" className="h-9 flex-1 min-w-[200px]" maxLength={300} />
              <input ref={input} type="file" multiple accept="image/*,.pdf,.heic,.heif" className="hidden" onChange={(e) => upload(e.target.files)} />
              <Button size="sm" className="h-9 gap-1.5" disabled={busy} onClick={() => input.current?.click()}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />} Add photos
              </Button>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">JPG, PNG, WebP, GIF, HEIC (kept as JPG) or PDF, up to 10 MB each.</p>

          {loading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Nothing attached yet.</p>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto">
              {items.map((a) => (
                <li key={a.id} className="rounded-lg border border-border overflow-hidden bg-card">
                  <a href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="block aspect-square bg-muted/40 flex items-center justify-center">
                    {a.is_image ? (
                      <img src={fileUrl(a.id)} alt={a.caption || a.file_name} loading="lazy" className="size-full object-cover" />
                    ) : (
                      <FileText className="size-10 text-muted-foreground" />
                    )}
                  </a>
                  <div className="p-2 text-[11px] space-y-0.5">
                    <p className="font-medium truncate" title={a.file_name}>{a.file_name}</p>
                    {a.caption && <p className="text-muted-foreground break-words">{a.caption}</p>}
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span>{new Date(a.created_on).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                      {canDelete && (
                        <button type="button" className="text-red-600 hover:text-red-700" onClick={() => remove(a)} title="Remove">
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
